import { Model, Relation } from "@nozbe/watermelondb";
import {
  field,
  immutableRelation,
  json,
  reader,
  text,
  writer,
} from "@nozbe/watermelondb/decorators";
import { Associations } from "@nozbe/watermelondb/Model";
import {
  SaleAdjustment as BaseAdjustment,
  SaleAdjustmentType,
} from "@towersystems/roam-common/lib/generated-types";
import { noopSanitizeJSON } from "../utils";
import Product from "./Product";
import Sale from "./Sale";
import { v4 as uuid } from "uuid";
import { calculateTax } from "../../../modules/point-of-sale/utils/tax-calculator";
import { calculateDiscountAmount } from "../../../modules/point-of-sale/utils/calculate-discounted-amount";
import ProductKit from "./ProductKit";

export type Adjustment = { localId: string; id?: string } & Omit<
  BaseAdjustment,
  "id" | "__typename"
>;

export type AddAdjustmentInput = {
  type: SaleAdjustmentType;
  amount: number;
  unitLoyaltyPointsUsed?: number;
};

export default class SaleItem extends Model {
  static table = "sale_items";

  static associations: Associations = {
    sale: { type: "belongs_to", key: "sale_id" },
  };

  @immutableRelation("sales", "sale_id") sale: Relation<Sale>;
  @immutableRelation("products", "product_id") product: Relation<Product>;
  @immutableRelation("product_kits", "product_kit_id")
  productKit: Relation<ProductKit>;
  @field("quantity") quantity: number;
  @field("base_price") basePrice: number;
  @json("adjustments", noopSanitizeJSON) adjustments: Adjustment[];
  @field("cost") cost: number;
  @field("tax_rate_percent") taxRatePercent: number;

  @text("external_id") externalId: string | null;
  @text("stock_code") stockCode: string | null;
  @text("stock_number") stockNumber: string | null;
  @text("serial_number") serialNumber: string | null;
  // per unit loyalty point earned
  @field("loyalty_point_earn_rate") loyaltyPointEarnRate: number;
  @json("metadata", noopSanitizeJSON) metadata: { [key: string]: any };
  @text("name") name: string | null;
  @text("customer_note") customerNote: string | null;
  @text("gift_card_number") giftCardNumber: string | null;

  get loyaltyPointEarned() {
    if (this.quantity <= 0) {
      return 0;
    }
    return this.loyaltyPointEarnRate * this.quantity;
  }

  get totalBasePrice() {
    return this.quantity * this.basePrice;
  }

  get tax() {
    return calculateTax(this.price, this.taxRatePercent);
  }

  get price() {
    return this.basePrice - this.unitAdjustments;
  }

  get totalPrice() {
    return this.price * this.quantity;
  }

  get totalTax() {
    return this.quantity * this.tax;
  }

  // alias for total adjustments
  get totalDiscount() {
    return this.totalAdjustments;
  }

  get unitAdjustments() {
    return this.adjustments.reduce((total, adjustment) => {
      return total + adjustment.amount;
    }, 0);
  }

  get totalAdjustments() {
    return this.adjustments.reduce((total, adjustment) => {
      return total + adjustment.amount * this.quantity;
    }, 0);
  }

  get totalLoyaltyPointEarned() {
    return this.quantity * (this.loyaltyPointEarned || 0);
  }

  get hasAdjustments() {
    return this.adjustments.length > 0;
  }

  async discountableItem_ASYNC() {
    const product = await this.product.fetch();
    return product.discountable;
  }

  async totalPossibleRedeemValue_ASYNC(points: number) {
    if (!(await this.discountableItem_ASYNC())) {
      return 0;
    }
    const product = await this.product.fetch();
    return this.quantity * (product.loyaltyPointRedeemValue * points);
  }

  async maxPossibleRedeemPoints_ASYNC() {
    if (!(await this.discountableItem_ASYNC())) {
      return 0;
    }
    const product = await this.product.fetch();
    // loyaltyPointRedeemValue per dollar
    return Math.ceil(this.totalPrice) * product.loyaltyPointRedeemValue;
  }

  async calculateTradePriceSavings() {
    const sale = await this.sale.fetch();
    const product = await this.product.fetch();

    const locationId = (await sale.location)._raw.id;

    const inventoryItem =
      product.locationInventoryPricing.find(
        (pricing) => pricing.locationId === locationId
      ) ?? product.locationInventoryPricing[0];

    if (!inventoryItem) {
      return 0;
    }

    const tradePrice = inventoryItem.tradePrice;

    if (!tradePrice) {
      return 0;
    }

    const tradeDiscontAmount = calculateDiscountAmount(
      inventoryItem.retailPrice,
      "price",
      tradePrice
    );

    return tradeDiscontAmount * this.quantity;
  }

  @writer async applyTradePrice() {
    const sale = await this.sale.fetch();
    const product = await this.product.fetch();
    const locationId = (await sale.location)._raw.id;

    const inventoryItem =
      product.locationInventoryPricing.find(
        (pricing) => pricing.locationId === locationId
      ) ?? product.locationInventoryPricing[0];

    if (!inventoryItem) {
      return;
    }

    const existingAdjustment = this.findAdjustmentByType(
      SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT
    );

    if (existingAdjustment) {
      await this.callWriter(() =>
        this.removeAdjustment(SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT)
      );
    }

    const tradePrice = inventoryItem.tradePrice;

    if (!tradePrice) {
      return;
    }

    const tradeDiscountAmount = calculateDiscountAmount(
      inventoryItem.retailPrice,
      "price",
      tradePrice
    );

    await this.callWriter(() =>
      this.addAdjustment({
        type: SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT,
        amount: tradeDiscountAmount,
      })
    );
  }

  @writer async updateCustomerNote(customerNote: string) {
    await this.callReader(() => this.assertCanBeModified());
    await this.update((item) => {
      item.customerNote = customerNote;
    });
  }

  @writer async updateSerialNumber(serialNumber: string) {
    await this.callReader(() => this.assertCanBeModified());
    await this.update((item) => {
      item.serialNumber = serialNumber;
    });
  }

  @writer async addAdjustment(input: AddAdjustmentInput) {
    await this.callReader(() => this.assertCanBeModified());
    await this.assertCanBeDiscounted();

    if (this.findAdjustmentByType(input.type)) {
      throw new Error("Adjustment already exists");
    }

    if (input.amount <= 0 || isNaN(input.amount)) {
      throw new Error("Adjustment amount must be greater than 0");
    }

    if (
      input.type === SaleAdjustmentType.LOYALTY_DISCOUNT &&
      (input?.unitLoyaltyPointsUsed || 0) <= 0
    ) {
      throw new Error("Loyalty points used must be greater than 0");
    }

    // if trade price adjustment, must remove all
    if (input.type === SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT) {
      await this.update((item) => {
        item.adjustments = [
          {
            ...input,
            localId: uuid(),
            unitLoyaltyPointsUsed: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
        ];
      });
      return;
    }

    await this.update((item) => {
      item.adjustments = [
        ...item.adjustments,
        {
          ...input,
          localId: uuid(),
          unitLoyaltyPointsUsed: input.unitLoyaltyPointsUsed || 0,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      ];
    });
  }

  @writer async removeAdjustment(type: SaleAdjustmentType) {
    await this.callReader(() => this.assertCanBeModified());
    await this.update((item) => {
      item.adjustments = item.adjustments.filter(
        (adjustment) => adjustment.type !== type
      );
    });
  }

  findAdjustmentByType = (type: SaleAdjustmentType) => {
    return this.adjustments.find((adjustment) => adjustment.type == type);
  };

  @writer async updateQuantity(quantity: number) {
    await this.callReader(() => this.assertCanBeModified());
    await this.update((item) => {
      item.quantity = quantity;
    });
  }

  @writer async increaseQuantity() {
    await this.callReader(() => this.assertCanBeModified());
    await this.update((item) => {
      if (item.quantity + 1 == 0) {
        item.quantity = 1;
      } else {
        item.quantity = item.quantity + 1;
      }
    });
  }

  @writer async decreaseQuantity() {
    await this.callReader(() => this.assertCanBeModified());
    await this.update((item) => {
      if (item.quantity > 1 || item.quantity < 0) {
        item.quantity = item.quantity - 1;
      } else {
        item.quantity = -1;
      }
    });
  }

  @writer async setName(name: string | null) {
    await this.callReader(() => this.assertCanBeModified());
    await this.update((item) => {
      item.name = name;
    });
  }

  @writer async updateMetadata(key: string, value: any) {
    await this.callReader(() => this.assertCanBeModified());
    await this.update((item) => {
      item.metadata = {
        ...item.metadata,
        [key]: value,
      };
    });
  }

  @writer async deleteItem() {
    await this.callReader(() => this.assertCanBeModified());
    await this.destroyPermanently();
  }

  @reader async assertCanBeModified() {
    const sale = await this.sale.fetch();
    await this.callReader(() => sale.assertCanModifySale());
  }

  assertCanBeDiscounted = async () => {
    if (!(await this.discountableItem_ASYNC())) {
      throw new Error("Item is not discountable");
    }

    // check if has trade price adjustment
    if (this.findAdjustmentByType(SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT)) {
      throw new Error("Trade price adjustment exists");
    }
  };
}
